import { useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../../api/apiClient";
import { getUserToken } from "../../function/AuthStorage";
import { getScope, isScopeActive, setScope } from "../../api/scopeSelection";
import { cachedTypes, liveTypes, useProjectTypes } from "../../services/ProjectTypesApi";
import "./ScopeBar.css";

/** تحديث 98: أنواع الشاشات الموحدة من سجل الأنواع (الأصلية، والجديد بعد اعتماد مساره). */
const typeList = (types) => liveTypes(types).map((t) => ({ code: t.code, label: t.nameAr }));
const TYPES = typeList(cachedTypes());

export const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

/** الأقسام/المسارات للعقود المختارة (وإلا لكل العقود) — مشترك بين شريط النطاق والصفحات التي تدمجه في فلاترها (تحديث 95). */
export function loadScopeDepartments(contracts, selectedIds) {
  const ids = (selectedIds && selectedIds.length ? selectedIds : contracts.map((c) => c.id)).slice(0, 40);
  return Promise.all(ids.map((id) => apiClient.get(`ContractWorkflow/contracts/${id}/departments`, { headers: { "X-ASF-Scope": "" } })
    .then((r) => (r.data || []).map((d) => ({ id: d.id, label: `${d.name}${ids.length > 1 ? " · " + (contracts.find((c) => c.id === d.contractId)?.contractNumber ?? "") : ""}` })))
    .catch(() => [])))
    .then((all) => all.flat());
}

export function MultiPick({ label, options, value, onChange, placeholder, disabled, onOpen, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const shown = options.filter((o) => !q || o.label.toLowerCase().includes(q.toLowerCase()));
  const chosen = options.filter((o) => value.includes(o.id));

  return (
    <div className="sb-field" ref={ref} data-testid={testId}>
      <span className="sb-label">{label}</span>
      <button type="button" className="sb-pick" disabled={disabled} onClick={() => { if (!open) onOpen?.(); setOpen(!open); }} aria-expanded={open}>
        {chosen.length === 0 ? <span className="sb-ph">{placeholder}</span> : (
          <span className="sb-chips">
            {chosen.slice(0, 2).map((o) => <span key={o.id} className="sb-chip">{o.label}</span>)}
            {chosen.length > 2 && <span className="sb-chip sb-chip--more">+{chosen.length - 2}</span>}
          </span>
        )}
      </button>
      {open && (
        <div className="sb-menu" role="listbox" aria-multiselectable="true">
          <input className="sb-search" placeholder="بحث…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          <div className="sb-list">
            {shown.length === 0 && <div className="sb-empty">لا نتائج</div>}
            {shown.map((o) => (
              <label key={o.id} className="sb-opt">
                <input type="checkbox" checked={value.includes(o.id)} onChange={() => onChange(toggle(value, o.id))} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
          {value.length > 0 && <button type="button" className="sb-link" onClick={() => onChange([])}>مسح الاختيار</button>}
        </div>
      )}
    </div>
  );
}

export const SCOPE_TYPE_LIST = TYPES;

export function ScopeBar() {
  const TYPES = typeList(useProjectTypes());
  const [contracts, setContracts] = useState([]);
  useEffect(() => {
    if (!getUserToken()) return;
    apiClient.get("ContractWorkflow/contracts")
      .then((r) => setContracts((Array.isArray(r.data) ? r.data : []).filter((c) => c.isActive !== false)))
      .catch(() => {});
  }, []);
  const applied = getScope();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(applied);
  const [depts, setDepts] = useState([]);

  const contractOpts = useMemo(
    () => contracts.map((c) => ({ id: c.id, label: [c.name, c.contractNumber].filter(Boolean).join(" — ") || String(c.id) })),
    [contracts]
  );

  // الأقسام/المسارات: للعقود المختارة (وإلا لكل عقود المستخدم)
  useEffect(() => {
    if (!open) return;
    let dead = false;
    loadScopeDepartments(contracts, draft.c).then((all) => { if (!dead) setDepts(all); });
    return () => { dead = true; };
  }, [open, draft.c.join(","), contracts.length]); // eslint-disable-line

  const active = isScopeActive(applied);
  const apply = (next) => { setScope(next); window.location.reload(); };

  const summary = [];
  if (applied.t.length) summary.push(applied.t.map((t) => TYPES.find((x) => x.code === t)?.label ?? t).join(" + "));
  if (applied.c.length) summary.push(`${applied.c.length} عقد`);
  if (applied.d.length) summary.push(`${applied.d.length} قسم`);
  if (applied.f || applied.e) summary.push(`${applied.f || "…"} → ${applied.e || "…"}`);

  return (
    <div className={`sb ${active ? "sb--active" : ""}`} data-testid="scope-bar">
      <button type="button" className="sb-toggle" onClick={() => { setDraft(getScope()); setOpen((v) => !v); }} aria-expanded={open}>
        <span className="sb-title">النطاق:</span>
        <span className="sb-sum" data-testid="scope-summary">{active ? summary.join(" · ") : "الكل"}</span>
        <span aria-hidden="true">{open ? "▴" : "▾"}</span>
      </button>
      {active && <button type="button" className="sb-clear" onClick={() => apply({})}>مسح الكل ✕</button>}

      {open && (
        <div className="sb-panel">
          <div className="sb-field">
            <span className="sb-label">نوع أمر العمل (اختيار متعدد)</span>
            <div className="sb-seg" role="group">
              <button type="button" className={`sb-seg__b ${draft.t.length === 0 ? "on" : ""}`} onClick={() => setDraft({ ...draft, t: [] })}>الكل</button>
              {TYPES.map((t) => (
                <button key={t.code} type="button" aria-pressed={draft.t.includes(t.code)}
                  className={`sb-seg__b ${draft.t.includes(t.code) ? "on" : ""}`}
                  onClick={() => setDraft({ ...draft, t: toggle(draft.t, t.code) })}>{t.label}</button>
              ))}
            </div>
          </div>
          <MultiPick label="العقود" placeholder="كل العقود" options={contractOpts} value={draft.c}
            onChange={(c) => setDraft({ ...draft, c, d: [] })} />
          <MultiPick label="الأقسام / المسارات" placeholder="كل الأقسام" options={depts} value={draft.d}
            onChange={(d) => setDraft({ ...draft, d })} />
          <div className="sb-field">
            <span className="sb-label">من</span>
            <input type="date" className="sb-date" value={draft.f} onChange={(e) => setDraft({ ...draft, f: e.target.value })} />
          </div>
          <div className="sb-field">
            <span className="sb-label">إلى</span>
            <input type="date" className="sb-date" value={draft.e} onChange={(e) => setDraft({ ...draft, e: e.target.value })} />
          </div>
          <div className="sb-actions">
            <button type="button" className="sb-apply" onClick={() => apply(draft)}>تطبيق</button>
            <button type="button" className="sb-reset" onClick={() => apply({})}>مسح الكل</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScopeBar;
